import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Hand-written, not generated: the transcription service's api-transcription SDK does not
// have these endpoints yet (regenerated from /api-docs outside this repo). Follows the same
// call convention as the rest of the app (environment.apiTranscription + apiPath), so replacing
// this with the generated client later is a drop-in swap, not a rewrite.

export type YoutubeSourceType = 'CHANNEL' | 'PLAYLIST';

export interface YoutubeSourceResponse {
	id: string;
	name: string;
	url: string;
	type: YoutubeSourceType;
	keywords: string | null;
	enabled: boolean;
	customScheduleTimes: string | null;
	lastCheckedAt: string | null;
	createdAt: string;
}

export interface YoutubeSourceRequest {
	name: string;
	url: string;
	keywords: string | null;
	enabled: boolean;
	customScheduleTimes: string | null;
}

export type YoutubeSubtitleKind = 'MANUAL' | 'AUTOMATIC' | 'NONE';
export type YoutubeSourcePreviewStatus = 'WOULD_DOWNLOAD' | 'ALREADY_EXISTS' | 'OLD' | 'NO_SUBTITLES' | 'ERROR';

export interface YoutubeSourcePreviewItem {
	title: string;
	url: string;
	publicationDate: string | null;
	subtitleKind: YoutubeSubtitleKind | null;
	status: YoutubeSourcePreviewStatus;
	errorMessage: string | null;
}

export interface YoutubeSourcePreviewResponse {
	items: YoutubeSourcePreviewItem[];
	totalMatching: number;
}

export interface YoutubeSourceDownloadResponse {
	downloadedCount: number;
	truncated: boolean;
}

export type YoutubeDownloadLogStatus = 'DOWNLOADED' | 'SKIPPED_NO_SUBTITLES' | 'SKIPPED_NO_KEYWORD' | 'SKIPPED_OLD' | 'ALREADY_EXISTS' | 'ERROR';

export interface YoutubeDownloadLogEntry {
	id: string;
	createdAt: string;
	sourceId: string;
	sourceName: string;
	videoTitle: string;
	videoUrl: string;
	status: YoutubeDownloadLogStatus;
	errorMessage: string | null;
	transcriptionId: string | null;
}

export interface YoutubeDownloadLogFilter {
	sourceId: string | null;
	status: YoutubeDownloadLogStatus | null;
}

// Only the fields this page actually reads from Spring's Page<T> JSON shape.
export interface PageResponse<T> {
	content: T[];
	totalElements: number;
	totalPages: number;
	number: number;
	size: number;
}

export interface YoutubeAutoDownloadSettings {
	enabled: boolean;
	generalScheduleTimes: string | null;
}

@Injectable({ providedIn: 'root' })
export class YoutubeSourceService {
	private readonly basePath = `${environment.apiTranscription}${environment.apiPath}/management/youtube-sources`;

	constructor(private http: HttpClient) {}

	getSources(): Observable<YoutubeSourceResponse[]> {
		return this.http.get<YoutubeSourceResponse[]>(this.basePath);
	}

	createSource(body: YoutubeSourceRequest): Observable<YoutubeSourceResponse> {
		return this.http.post<YoutubeSourceResponse>(this.basePath, body);
	}

	updateSource(id: string, body: YoutubeSourceRequest): Observable<YoutubeSourceResponse> {
		return this.http.put<YoutubeSourceResponse>(`${this.basePath}/${id}`, body);
	}

	deleteSource(id: string): Observable<void> {
		return this.http.delete<void>(`${this.basePath}/${id}`);
	}

	previewSource(id: string): Observable<YoutubeSourcePreviewResponse> {
		return this.http.get<YoutubeSourcePreviewResponse>(`${this.basePath}/${id}/preview`);
	}

	downloadSource(id: string): Observable<YoutubeSourceDownloadResponse> {
		return this.http.post<YoutubeSourceDownloadResponse>(`${this.basePath}/${id}/download`, {});
	}

	// Fires the background batch run and returns immediately - there is no result to wait for,
	// progress shows up afterwards in the download log and in each source's lastCheckedAt.
	downloadSourcesBatch(sourceIds: string[]): Observable<void> {
		return this.http.post<void>(`${this.basePath}/download-batch`, { sourceIds });
	}

	searchDownloadLog(filter: YoutubeDownloadLogFilter, page: number, size: number): Observable<PageResponse<YoutubeDownloadLogEntry>> {
		const params = { page: String(page), size: String(size) };
		return this.http.post<PageResponse<YoutubeDownloadLogEntry>>(`${this.basePath}/download-log/filter`, filter, { params });
	}

	getSettings(): Observable<YoutubeAutoDownloadSettings> {
		return this.http.get<YoutubeAutoDownloadSettings>(`${this.basePath}/settings`);
	}

	updateSettings(body: YoutubeAutoDownloadSettings): Observable<YoutubeAutoDownloadSettings> {
		return this.http.put<YoutubeAutoDownloadSettings>(`${this.basePath}/settings`, body);
	}
}
